import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Rocket, CheckCircle2, Circle, Loader2, GraduationCap, Clock,
  BookOpen, FlaskConical, Gamepad2, Wrench, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICON = { Article: BookOpen, Research: FlaskConical, Practice: Gamepad2, Tool: Wrench };
const TIER_COLORS = {
  Beginner: { accent: '#10B981', badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
  Intermediate: { accent: '#007AFF', badge: 'bg-[#007AFF]/10 text-[#007AFF] border-[#007AFF]/30' },
  Advanced: { accent: '#C5A059', badge: 'bg-[#C5A059]/10 text-[#C5A059] border-[#C5A059]/30' },
};

export default function StartHere() {
  const { isAuthenticated, login, getToken } = useAuth();
  const [path, setPath] = useState([]);
  const [completed, setCompleted] = useState(new Set());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/learning-path`).then(r => setPath(r.data.path)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      axios.get(`${API}/learning-path/progress`, { headers: { Authorization: `Bearer ${getToken()}` } })
        .then(r => {
          setCompleted(new Set(r.data.completed_steps));
          setStats(r.data);
        }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const toggle = async (stepId) => {
    if (!isAuthenticated) { login(); return; }
    try {
      const res = await axios.post(`${API}/learning-path/toggle/${stepId}`, {},
        { headers: { Authorization: `Bearer ${getToken()}` } });
      const next = new Set(completed);
      if (res.data.completed) { next.add(stepId); toast.success('Step completed!'); }
      else next.delete(stepId);
      setCompleted(next);
      setStats(res.data);
    } catch {
      toast.error('Could not update progress');
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="start-here-page">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C5A059] to-[#D4AF37] flex items-center justify-center">
              <Rocket size={20} className="text-black" />
            </div>
            <h1 className="font-manrope font-bold text-3xl sm:text-4xl text-white">
              Start <span className="text-[#C5A059]">Here</span>
            </h1>
          </div>
          <p className="text-gray-400 text-sm max-w-2xl">
            Your guided path from complete beginner to institutional-level thinker.
            Work through each tier in order, tick off steps, and track your progress.
          </p>
        </div>

        {/* Overall progress */}
        {isAuthenticated && stats && (
          <div className="bg-[#111111] border border-[#C5A059]/30 rounded-xl p-5 mb-8" data-testid="overall-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-semibold text-sm inline-flex items-center gap-2">
                <GraduationCap size={16} className="text-[#C5A059]" /> Your journey
              </span>
              <span className="text-[#C5A059] font-bold text-sm" data-testid="overall-pct">{stats.pct}% complete</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#C5A059] to-[#D4AF37] rounded-full transition-all" style={{ width: `${stats.pct}%` }} />
            </div>
            <p className="text-gray-600 text-xs mt-2">{stats.completed} of {stats.total} steps completed</p>
          </div>
        )}
        {!isAuthenticated && (
          <div className="flex items-start gap-3 bg-[#C5A059]/5 border border-[#C5A059]/20 rounded-xl px-4 py-3 mb-8">
            <AlertTriangle size={15} className="text-[#C5A059] mt-0.5 flex-shrink-0" />
            <p className="text-xs text-gray-400">
              <button onClick={login} className="text-[#C5A059] font-semibold hover:underline" data-testid="signin-to-track">Sign in</button>{' '}
              to save your progress and get a "Continue where you left off" shortcut on the homepage.
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 size={30} className="animate-spin text-[#C5A059]" /></div>
        ) : (
          <div className="space-y-10">
            {path.map((tier, ti) => {
              const colors = TIER_COLORS[tier.tier];
              const tierStats = stats?.tiers?.[tier.tier];
              return (
                <div key={tier.tier} data-testid={`tier-${tier.tier.toLowerCase()}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-3">
                      <span className="font-manrope font-bold text-2xl text-white">
                        <span style={{ color: colors.accent }}>{ti + 1}.</span> {tier.tier}
                      </span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${colors.badge}`}>
                        {tier.steps.length} steps
                      </span>
                    </div>
                    {tierStats && (
                      <span className="text-xs text-gray-500" data-testid={`tier-pct-${tier.tier.toLowerCase()}`}>
                        {tierStats.completed}/{tierStats.total} · <span style={{ color: colors.accent }}>{tierStats.pct}%</span>
                      </span>
                    )}
                  </div>
                  <p className="text-gray-500 text-sm mb-4">{tier.tier_desc}</p>
                  {tierStats && (
                    <div className="h-1 bg-white/10 rounded-full overflow-hidden mb-4">
                      <div className="h-full rounded-full transition-all" style={{ width: `${tierStats.pct}%`, background: colors.accent }} />
                    </div>
                  )}

                  <div className="space-y-2">
                    {tier.steps.map((step, si) => {
                      const done = completed.has(step.step_id);
                      const Icon = TYPE_ICON[step.type] || BookOpen;
                      return (
                        <div key={step.step_id}
                          className={`flex items-center gap-3 bg-[#111111] border rounded-xl px-4 py-3 transition-colors ${done ? 'border-emerald-500/30 bg-emerald-500/[0.03]' : 'border-white/10 hover:border-white/20'}`}
                          data-testid={`step-${step.step_id}`}>
                          <button onClick={() => toggle(step.step_id)}
                            className="flex-shrink-0 transition-transform hover:scale-110"
                            data-testid={`toggle-${step.step_id}`}
                            title={done ? 'Mark incomplete' : 'Mark complete'}>
                            {done
                              ? <CheckCircle2 size={22} className="text-emerald-500" />
                              : <Circle size={22} className="text-gray-600 hover:text-[#C5A059]" />}
                          </button>
                          <Link to={step.path} className="flex-1 min-w-0 group">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-sm font-semibold group-hover:text-[#C5A059] transition-colors ${done ? 'text-gray-500 line-through' : 'text-white'}`}>
                                {si + 1}. {step.title}
                              </span>
                              <span className="inline-flex items-center gap-1 text-[10px] text-gray-600 bg-white/5 px-2 py-0.5 rounded-full">
                                <Icon size={10} /> {step.type}
                              </span>
                              {step.pro && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#C5A059]/20 text-[#C5A059] border border-[#C5A059]/40">PRO</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5 truncate">{step.desc}</p>
                          </Link>
                          <span className="flex-shrink-0 text-[11px] text-gray-600 inline-flex items-center gap-1">
                            <Clock size={11} /> {step.minutes}m
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {isAuthenticated && stats?.pct === 100 && (
          <div className="mt-10 text-center bg-gradient-to-r from-[#1a1508] to-[#0D0D0D] border border-[#C5A059] rounded-xl p-8" data-testid="path-complete">
            <GraduationCap size={36} className="text-[#C5A059] mx-auto mb-3" />
            <h2 className="text-white font-manrope font-bold text-2xl mb-2">Path Complete!</h2>
            <p className="text-gray-400 text-sm mb-4">You've finished the full DigitalGoldGang learning journey. Keep sharpening your edge:</p>
            <Link to="/analysis"><Button className="bg-[#C5A059] text-black hover:bg-[#D4AF37] font-semibold">Explore Institutional Analysis</Button></Link>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
